<template>
  <section
    :class="sectionBgClass"
    class="w-full overflow-hidden"
  >
    <div :class="[(data?.containerFull || isPlain) ? 'w-full' : 'container px-0 lg:px-4']">
      <!-- Title -->
      <UiHeading
        v-if="data?.heading"
        :pin="data?.heading?.pin"
        :title="data?.heading?.title"
        :description="data?.heading?.description"
        :button="data?.heading?.button"
        :color="data?.heading?.color"
        :position="data?.heading?.position"
        v-aos="animate && 'zoom-out'"
      />
      <!-- Title -->

      <!-- Card -->
      <!-- Slider -->
      <template v-if="shouldShowSlider">
        <PluginsFancybox :options="fancyboxOptions">
          <div v-aos="animate && 'fade-up'" class="w-full">
            <PluginsCarousel
              :data="enabledItems"
              :pagination="true"
              :navigation="false"
              :per="data?.column === 'column-4' ? 4 : 3"
              type="Card"
              class="cards-slider"
              :class="carouselClass"
            >
              <template v-slot="{ slide }">
                <UiCardGallery
                  :pin="slide?.pin"
                  :title="slide?.title"
                  :description="slide?.description"
                  :link="slide?.link"
                  :button="slide?.button"
                  :media="slide?.media"
                  :position="data?.position"
                  :mediaSize="data?.mediaSize"
                  :background="data.backgroundColor"
                  class="h-full"
                />
              </template>
            </PluginsCarousel>
          </div>
        </PluginsFancybox>
      </template>
      <!-- Slider -->

      <!-- Non Slider -->
      <template v-else>
        <PluginsFancybox :options="fancyboxOptions">
          <div
            v-aos="animate && 'fade-up'"
            class="w-full"
            :class="galleryGridClass"
          >
            <template v-for="(item, index) in enabledItems">
              <UiCardGallery
                :pin="item?.pin"
                :title="item?.title"
                :description="item?.description"
                :link="item?.link"
                :target="item?.target"
                :media="item?.media"
                :position="data?.position"
                :mediaSize="data?.mediaSize"
                :background="data?.backgroundColor"
                :isotope="data?.isotope"
              />
            </template>
          </div>
        </PluginsFancybox>
      </template>
      <!-- Non Slider -->
      <!-- Card -->

      <!-- Button -->
      <div
        v-if="data?.showMoreLink && data?.showMoreLink?.length > 0"
        class="flex mt-4 md:mt-6 lg:mt-8 xl:mt-10 justify-center"
      >
        <AtomsButton
          :label="$t('Show More')"
          :link="data?.showMoreLink"
          :color="buttonColor"
          size="medium"
          :enabled="true"
        />
      </div>
      <!-- Button -->
    </div>
  </section>
</template>
<script setup>
import { computed } from "vue";
import { useLocalePath } from "#i18n";
import { useIsMobile } from "~/composables/utils";
import { sectionOuterSpacingClass } from "~/composables/sectionSpacing";
const props = defineProps({
  data: { type: Object },
  animate: { type: Boolean, default: false },
  isPlain: { type: Boolean, default: false },
});

const { isMobile } = useIsMobile();

const sectionBgClass = computed(() => {
  if (props.isPlain) return 'w-full pt-10 lg:pt-16'; // Add padding to separate from rich text
  let bg = '';
  const bgColor = props.data?.backgroundColor;
  if (bgColor === 'first') bg = 'bg-brand-first' + (props.data?.backgroundColor !== 'transparent' ? ' py-10 lg:py-20' : '');
  else if (bgColor === 'second') bg = 'bg-brand-second' + (props.data?.backgroundColor !== 'transparent' ? ' py-10 lg:py-20' : '');
  else if (bgColor === 'third') bg = 'bg-brand-third' + (props.data?.backgroundColor !== 'transparent' ? ' py-10 lg:py-20' : '');
  else if (bgColor === 'dark-first') bg = 'bg-brand-dark' + (props.data?.backgroundColor !== 'transparent' ? ' py-10 lg:py-20' : '');
  else if (bgColor === 'dark-second') bg = 'bg-brand-dark-second' + (props.data?.backgroundColor !== 'transparent' ? ' py-10 lg:py-20' : '');
  else if (bgColor === 'light-first') bg = 'bg-brand-light' + (props.data?.backgroundColor !== 'transparent' ? ' py-10 lg:py-20' : '');
  else if (bgColor === 'light-second') bg = 'bg-brand-light-second' + (props.data?.backgroundColor !== 'transparent' ? ' py-10 lg:py-20' : '');
  return [bg, sectionOuterSpacingClass(props.data)];
});

const carouselClass = computed(() => {
  const bg = props.data?.backgroundColor;
  return (bg === 'first' || bg === 'second' || bg === 'dark-first' || bg === 'dark-second') ? '' : 'non-bg';
});

const galleryGridClass = computed(() => {
  const isIsotope = props.data?.isotope;
  const col = props.data?.column;
  if (isIsotope) {
    if (col === 'column-4') return 'columns-2 gap-3 space-y-3 sm:gap-4 sm:space-y-4 md:gap-6 md:space-y-6 md:columns-3 xl:columns-4';
    if (col === 'column-3') return 'columns-2 gap-3 space-y-3 sm:gap-4 sm:space-y-4 md:gap-6 md:space-y-6 md:columns-3';
    return 'columns-2 gap-3 space-y-3 sm:gap-4 sm:space-y-4 md:gap-6 md:space-y-6';
  }
  if (col === 'column-4') return 'grid grid-cols-2 gap-3 sm:gap-4 md:gap-6 md:grid-cols-2 xl:grid-cols-4';
  if (col === 'column-3') return 'grid grid-cols-2 gap-3 sm:gap-4 md:gap-6 lg:grid-cols-3';
  return 'grid grid-cols-2 gap-3 sm:gap-4 md:gap-6';
});

const buttonColor = computed(() => props.data?.heading?.color === 'white' ? 'white-outline' : 'black-outline');

const shouldShowSlider = computed(() => {
  return props.data?.slider;
});

const fancyboxOptions = {
  Carousel: {
    infinite: false,
  },
};

const enabledItems = computed(() => props.data?.items?.filter(item => item.enabled) || []);
</script>
